import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { domainKeys } from '@/hooks/api/query-keys';
import { useWorkspaceStore } from '@/stores/workspace-store';

// ============ Types ============

/** The DNS record that proves the company controls a domain (API-spelled). */
export interface DomainVerificationRecord {
  type: 'TXT';
  host: string;
  value: string;
}

export interface Domain {
  id: string;
  domain: string;
  isPrimary?: boolean;
  recipientCount: number;
  /** 'Active' | 'Pending Verification' | 'Suspended' | … as the domains table stores it. */
  status: string;
  /** Set only by a proof (DNS, or a Google / Microsoft connect). */
  verifiedAt: string | null;
  /**
   * GET /domains only: what to publish for an unproven domain. null once it is
   * proven, and null for a row born without a token — its first Verify mints
   * one. Other endpoints return no such key.
   */
  verificationRecord?: DomainVerificationRecord | null;
}

/** A domain counts as proven only when the API says both halves. */
export function isDomainVerified(d: Pick<Domain, 'status' | 'verifiedAt'>): boolean {
  return d.status === 'Active' && d.verifiedAt !== null;
}

export interface DomainVerificationResult {
  id: string;
  domain: string;
  status: string;
  verified: boolean;
  message: string;
}

export interface DomainsListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginatedDomains {
  data: Domain[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface CreateDomainDto {
  // BE CreateDomainDto expects `domainName` (class-validator @Matches). Sending
  // `domain` makes domainName undefined → 400 Bad Request → "Failed to add domain".
  domainName: string;
}

// ============ Query Hooks ============

/** Max page size the BE accepts (PaginationDto @Max). Asking for more 400s. */
const DOMAINS_PAGE_SIZE = 500;

/**
 * Flat list of ALL domains (array). Used by callers that need the full set
 * (recipient pickers, integration wizards, badge scoping).
 *
 * Pages through the server-paginated endpoint at the BE's max page size and
 * concatenates every page, instead of asking for a single oversized
 * `limit=1000` (which 400s against the BE's `@Max(500)` cap — the wrong way to
 * "fetch all"). One request covers any realistic workspace; the loop only
 * continues if a workspace somehow exceeds 500 domains.
 *
 * GET /domains is read:Domain: a caller whose page is open to roles without it
 * passes `enabled: false` for them, rather than meeting a 403 on every visit.
 */
export function useDomains(options: { enabled?: boolean } = {}) {
  // SI5 (audit 2026-09-25) — scoped by the active workspace (the one
  // `x-workspace-id` carries). After a switch the CSV import wizard remounts,
  // and an unscoped key handed it the PREVIOUS workspace's cached list, whose
  // lone domain it auto-selected before the refetch landed (the FE7 lesson).
  // Prefix invalidation (`domainKeys.all`) still matches.
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  return useQuery({
    queryKey: [...domainKeys.list({ all: true }), workspaceId] as const,
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const all: Domain[] = [];
      let page = 1;
      // Bounded loop — stop when a page returns fewer than a full page (last
      // page) or the envelope reports no further pages.
      for (;;) {
        const res = await fetchApi<Domain[] | PaginatedDomains>(
          `/domains?page=${page}&limit=${DOMAINS_PAGE_SIZE}`,
        );
        if (Array.isArray(res)) {
          // Legacy/array response — no pagination envelope, so it's the full set.
          all.push(...res);
          break;
        }
        all.push(...res.data);
        if (!res.hasNext || res.data.length < DOMAINS_PAGE_SIZE) break;
        page += 1;
      }
      return all;
    },
  });
}

/**
 * Server-side paginated + searchable domains. Used by the workspace settings
 * Domains card. Returns the full BE envelope ({ data, total, totalPages, … }).
 */
export function useDomainsPaginated(params: DomainsListParams) {
  const { page = 1, limit = 5, search = '' } = params;
  return useQuery({
    queryKey: domainKeys.list({ page, limit, search }),
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search.trim()) qs.set('search', search.trim());
      const res = await fetchApi<PaginatedDomains>(`/domains?${qs.toString()}`);
      return res;
    },
    placeholderData: (prev) => prev,
  });
}

// ============ Mutation Hooks ============

export function useCreateDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDomainDto) =>
      fetchApi<Domain>('/domains', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
  });
}

export function useDeleteDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<void>(`/domains/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
  });
}

/**
 * POST /domains/:id/verify — the DNS TXT proof. A refusal is the usual first
 * answer (the record is not published yet) and it can MINT the token of a row
 * born without one, so the list is re-read whatever the outcome.
 */
export function useVerifyDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<DomainVerificationResult>(`/domains/${id}/verify`, {
        method: 'POST',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
  });
}

export function useSetPrimaryDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<Domain>(`/domains/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isPrimary: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.all });
    },
  });
}
